import { assetUrl } from '../api/assets';

/**
 * Structured asset-path builders for the compositor (image_assets.md §4, FABLE5_BUILD_PROMPT.md
 * §13.2). This is the only place the compositor's asset ID conventions live — every function
 * here assembles the class-specific ID from its parts, then delegates to `assetUrl`
 * (web/src/api/assets.ts) to resolve the directory. `assetUrl` stays the single source of truth
 * for prefix-to-directory resolution; this file stays the single source of truth for how those
 * IDs are built from species/stage/slot/item parts. The client never stores or receives a URL —
 * every path is derived by convention.
 */

export type PetBaseVariant = 'base' | 'eat' | 'joy';

const missingPetBaseVariants = new Set<string>();

function petBaseKey(species: string, stage: string, variant: PetBaseVariant): string {
  return `${species}/${stage}/${variant}`;
}

/** Remember a missing eat/joy still so later pokes/feeds/grooms stay on `_base` (no repeat 404). */
export function rememberMissingPetBase(species: string, stage: string, variant: PetBaseVariant): void {
  if (variant === 'base') return;
  missingPetBaseVariants.add(petBaseKey(species, stage, variant));
}

/** Test-only: isolate the 404 memo across cases. */
export function resetMissingPetBases(): void {
  missingPetBaseVariants.clear();
}

export function petBasePath(species: string, stage: string, variant: PetBaseVariant = 'base'): string {
  const resolved = variant !== 'base' && missingPetBaseVariants.has(petBaseKey(species, stage, variant))
    ? 'base'
    : variant;
  return assetUrl(`pet_${species}_${stage}_${resolved}`);
}

export function itemIconPath(code: string): string {
  return assetUrl(`item_${code}_icon`);
}

export function keeperBodyPath(build: string | number, tone: string | number): string {
  return assetUrl(`kpr_body_${build}_${tone}`);
}

/**
 * The portrait land/world map canvas, declared once (image_assets.md §5.5: 1024x2216, the 390:844
 * phone shape). `MapPanZoom` needs it to build the fit box the painting is contained in, and pins
 * are positioned as percentages of that box — so a stale pair here does not merely letterbox
 * wrongly, it slides every pin off its landmark.
 *
 * <p>It was the literal `[1080, 1920]` written out at both call sites and in five test fixtures,
 * with the matching pair repeated in three CSS rules. Moving the art to 1024x2216 meant finding all
 * ten by grep. The CSS half now reads `--map-canvas-w`/`--map-canvas-h`, declared on the shared hub
 * root in base.css; this is the TS half. **Change both together, or the fit box and the surface
 * disagree.**
 */
export const LAND_MAP_ASPECT: readonly [number, number] = [1024, 2216];

/** The world overview's LANDSCAPE master (`map_world_overview.png`, image_assets.md §5.5) — what
 *  `/world` renders at the Studio tiers (Task 8), where the centre column is wide. Same rule as
 *  `LAND_MAP_ASPECT`: the CSS twin is `.world-hub-landscape`'s `--map-canvas-*` pair in base.css.
 *  **Change both together, or the fit box and the surface disagree.** */
export const WORLD_MAP_LANDSCAPE_ASPECT: readonly [number, number] = [1920, 1080];

/** image_assets.md §5.5/§11.3 — the landscape master and the portrait crop are two distinct
 *  full-size assets (never a client-side downscale), selected by which one the device needs. */
export function landMapPath(landCode: string, crop: 'portrait' | 'landscape'): string {
  return crop === 'portrait'
    ? assetUrl(`land_${landCode}_map_portrait`)
    : assetUrl(`land_${landCode}_map`);
}

/** World hub overview — one continent, not a land's own map. Portrait is what `/world` renders. */
export function worldMapPath(crop: 'portrait' | 'landscape'): string {
  return crop === 'portrait'
    ? assetUrl('map_world_overview_portrait')
    : assetUrl('map_world_overview');
}

export function npcPortraitPath(npcCode: string): string {
  return assetUrl(`npc_${npcCode}_neutral`);
}

export type EnemyPose = 'idle' | 'grit' | 'faint';

/** image_assets.md §5.10 — `enemy_<code>_{idle|grit|faint}`. */
export function enemySpritePath(enemyCode: string, pose: EnemyPose = 'idle'): string {
  return assetUrl(`enemy_${enemyCode}_${pose}`);
}

/**
 * Encounter content code → enemy art code (image_assets.md §5.10).
 * `enc_ember_cinderwisp` → `cinderwisp`; `boss_ember_ashvane_trial` → `ashvane`.
 * Returns null when the code is not the usual `enc_|boss_<land>_<creature>` shape.
 */
export function encounterEnemyCode(encounterCode: string): string | null {
  const m = /^(?:enc|boss)_[a-z0-9]+_(.+)$/.exec(encounterCode);
  if (!m) return null;
  return m[1].replace(/_trial$/, '');
}

/**
 * Encounter / boss code token → land code for `bd_battle_<landCode>`.
 * Tokens match content naming (`enc_hearth_*`, `boss_ember_*`, `enc_port_*`, …).
 */
const ENCOUNTER_LAND_TOKEN: Record<string, string> = {
  hearth: 'hearthollow',
  ember: 'emberfall_reach',
  glass: 'the_glasswood',
  noct: 'nocturne_basin',
  port: 'cinderport',
  tide: 'tidewrack_cove',
};

/** Lands (plus the arena, once its own stage ships — live-PvP seasons P8) that already have an
 *  intaken battle stage. Others fall back to the Thatchwell pilot. Exported so `stageForBattle`'s
 *  own test can assert both branches WITHOUT hardcoding which one is live today (`'arena'` joins
 *  this set the day `bd_battle_arena` is intake-processed, and that test must keep passing
 *  either way). */
export const SHIPPED_BATTLE_STAGES = new Set(['hearthollow', 'emberfall_reach', 'the_glasswood', 'cinderport', 'nocturne_basin', 'tidewrack_cove', 'arena']);

/** Display names for battle back pill (`← Thatchwell`) — mirrors content land `name`. */
const LAND_DISPLAY_NAMES: Record<string, string> = {
  hearthollow: 'Thatchwell',
  tidewrack_cove: 'Pebblecove',
  emberfall_reach: 'Cindermill',
  the_glasswood: 'Prismorchard',
  cinderport: 'Harborloop',
  nocturne_basin: 'Lunapool',
};

export function landCodeFromEncounter(encounterCode: string | null | undefined): string | null {
  if (!encounterCode) return null;
  const m = /^(?:enc|boss)_([a-z0-9]+)_/.exec(encounterCode);
  if (!m) return null;
  return ENCOUNTER_LAND_TOKEN[m[1]] ?? null;
}

export function landDisplayName(landCode: string | null | undefined): string {
  if (!landCode) return 'World';
  return LAND_DISPLAY_NAMES[landCode] ?? 'World';
}

/**
 * Back affordance for an in-shell sub-screen reached from a land hotspot (`.world-back-link`):
 * the land it was tapped from when that land is known, else the World hub. **Label and href are
 * derived together and always agree** — the whole point of this helper, since the bug it closes
 * was five screens hardcoding `← Thatchwell` over a link that went to `/world` (docs/SCREENS.md
 * "Sub-screen back link"). An unknown/absent code degrades to the hub rather than building
 * `/world/lands/<garbage>`, so a hand-typed or bookmarked URL carrying junk still lands somewhere
 * real. Reuses `LAND_DISPLAY_NAMES` — the same map `landDisplayName` (and the Battle back pill)
 * already reads — so a new land needs one row, not two.
 *
 * `name` is the bare destination ('Pebblecove', or 'World' for the fallback) for the one caller
 * that cannot show the label itself: `ShopScreen`'s header back control is arrow-only (the word
 * did not fit beside the longest shop name), so its accessible name is built from this rather than
 * by slicing the arrow back off `label` — same single derivation, no string surgery.
 */
export function landBackLink(
  landCode: string | null | undefined,
): { label: string; href: string; name: string } {
  const name = landCode ? LAND_DISPLAY_NAMES[landCode] : undefined;
  return name
    ? { label: `← ${name}`, href: `/world/lands/${landCode}`, name }
    : { label: '← World', href: '/world', name: 'World' };
}

/** image_assets.md §5.11 — per-land battle stage; pilot-only lands reuse Thatchwell. */
export function battleStagePath(landCode: string | null | undefined): string {
  const land = landCode && SHIPPED_BATTLE_STAGES.has(landCode) ? landCode : 'hearthollow';
  return assetUrl(`bd_battle_${land}`);
}

/**
 * Which stage `battleStagePath` should paint behind a battle screen, for ANY `BattleViewDto.kind`
 * — live-PvP seasons §3.8/P8. An arena battle (`arena_duel`/`arena_practice`) gets its own stage
 * once `bd_battle_arena` ships (checked through `SHIPPED_BATTLE_STAGES`, never hardcoded — the
 * whole point is that flipping this on is a content intake, not a code change); until then it
 * fights on Emberfall explicitly, per the design doc's own call, rather than falling through to
 * whatever land the (always-null) `encounterCode` would resolve to. Every other kind (`pve`,
 * `coop`, and the legacy `arena_attack`, which carries no `encounterCode` either) keeps the
 * existing per-land derivation, defaulting to Thatchwell exactly as `battleStagePath` always has.
 */
export function stageForBattle(kind: string, encounterCode: string | null | undefined): string {
  if (kind === 'arena_duel' || kind === 'arena_practice') {
    return SHIPPED_BATTLE_STAGES.has('arena') ? 'arena' : 'emberfall_reach';
  }
  return landCodeFromEncounter(encounterCode) ?? 'hearthollow';
}

/**
 * Does this battle kind seat COMPANIONS on both sides? Every arena kind does — a duel and a
 * practice bout each face a live keeper's saved loadout, and the legacy `arena_attack` faces a
 * stored defence loadout (which is why `ActorDto.stage`'s own javadoc lists "an arena defender"
 * alongside a PvE enemy as the two actors that can carry a null stage). `pve` and `coop` do not.
 *
 * Two things turn on it. Sprite choice: an enemy-band actor is drawn from the ENEMY art set
 * (`enemy_<creature>_<pose>`), and a companion species has no such file — see `BattleFighter`'s
 * `pvp` prop for the blank this produced in every duel. And D2 (Task 14 review, CRITICAL): gore is
 * PvE-enemies-only, and this is what tells a companion in the enemy band apart from a creature.
 *
 * Lives here, beside `stageForBattle`, because it answers the same question that one does: what
 * art does this battle kind use? One derivation, so the battle screen and the duel replay cannot
 * disagree.
 *
 * An EXPLICIT set, deliberately, not `kind.startsWith('arena')`: a future `arena_*` kind must opt
 * in by being listed here rather than inheriting the answer from its own name. A prefix test
 * silently answers for kinds nobody has considered yet, and the failure direction of a wrong
 * answer is another player's companion being procedurally shattered.
 */
const KEEPER_VS_KEEPER_KINDS = new Set(['arena_duel', 'arena_practice', 'arena_attack']);

export function isKeeperVsKeeper(kind: string): boolean {
  return KEEPER_VS_KEEPER_KINDS.has(kind);
}

/**
 * `ScreenBackdrop` id for a forage spot, by the land it sits on (docs/SCREENS.md "Forage screen
 * overhaul"). Returns the land's shipped battle stage, because there is no `bd_forage_*` row in
 * `assets/manifest.json` today and inventing one would render nothing: forage was the only place
 * screen in the game with no art at all, and the six battle stages are the paintings of these six
 * lands that DO exist. When per-spot forage art is drawn, change the template here — every caller
 * passes a land code and nothing else, so this is the one line that moves.
 *
 * Null for an unknown land (a bookmarked URL, a land whose stage hasn't been intaken) rather than
 * a guessed id: `ScreenBackdrop` already unmounts itself on a failed load, but not asking for a
 * file that cannot exist is cheaper than relying on that. Deliberately NOT sharing
 * `battleStagePath`'s Thatchwell fallback — a Lunapool spot backed by Thatchwell's meadow would be
 * a wrong place, which is worse here than no place. Same set, different failure policy.
 */
export function forageBackdropId(landCode: string | null | undefined): string | null {
  return landBackdropId(landCode);
}

/**
 * The general form of the helper above, added when the Welcome Basket sheet needed the same thing
 * (docs/SCREENS.md "Welcome Basket"): the art that stands for a LAND, for any screen or sheet that
 * wants to feel like it is somewhere.
 *
 * `forageBackdropId` now delegates here rather than being duplicated — its name is still correct
 * for its own caller, and the day a real `bd_forage_*` row exists it can stop delegating without
 * disturbing anyone else. Same null-for-unknown-land rule and the same reasoning: `ScreenBackdrop`
 * unmounts itself on a failed load, but not asking for an id we know is absent is cheaper than
 * asking and recovering.
 */
export function landBackdropId(landCode: string | null | undefined): string | null {
  return landCode && SHIPPED_BATTLE_STAGES.has(landCode) ? `bd_battle_${landCode}` : null;
}

/** The `badge_*`-coded badges that have a `ui_badge_<code-without-badge_>` row. Explicit, unlike
 *  the `bg_*` family below, because most `badge_*` codes have NO art and `UiSticker` would
 *  otherwise ask for a file that is not there; a badge joins this set the day its row is
 *  intake-processed (`ui_badge_arena_s01` landed 2026-09-18, live-PvP seasons P4). */
const SHIPPED_BADGE_STICKERS = new Set(['badge_arena_s01']);

/** `ui_badge_<code-without-bg_>` — Phase 5 Task 17 convention: every `bg_*` row has art. A
 *  `badge_*` code resolves only through `SHIPPED_BADGE_STICKERS`; everything else is null, and
 *  `BadgeMark` draws the shared rosette glyph. */
export function badgeStickerId(badgeCode: string): string | null {
  if (badgeCode.startsWith('bg_')) return `ui_badge_${badgeCode.slice(3)}`;
  if (SHIPPED_BADGE_STICKERS.has(badgeCode)) return `ui_badge_${badgeCode.slice('badge_'.length)}`;
  return null;
}

/** `ui_banner_<eventCode>`. Null when no banner row exists for that event. */
export function eventBannerId(eventCode: string): string | null {
  return eventCode === 'glasswood_lanterns' ? `ui_banner_${eventCode}` : null;
}

export function minigameStickerId(gameCode: string): string {
  return `ui_minigame_${gameCode}`;
}

/** Shared world-hub land pin (`ui_icon_map_pin`). One sticker for all six lands. */
export const WORLD_LAND_PIN_STICKER_ID = 'ui_icon_map_pin';

/** Content `banner_hearth` → manifest `ui_banner_hearth`. */
export function guildBannerStickerId(bannerCode: string): string {
  return `ui_${bannerCode}`;
}

/** Content `decor_lantern` → manifest `ui_decor_lantern`. */
export function guildDecorStickerId(decorCode: string): string {
  return `ui_${decorCode}`;
}

/**
 * Land activity code → minigame `code` (content `config.game`). Hand-mirrored: GET /world
 * does not expose activity config, same gap `guildDecor.ts` documents.
 */
export const MINIGAME_ACTIVITY_TO_GAME: Record<string, string> = {
  hearthollow_fair_green: 'fair_green_crates',
  tidewrack_regatta_ring: 'regatta_cast',
  emberfall_forge_games: 'forge_lanes',
  glasswood_echo_court: 'echo_prisms',
  cinderport_clockwork_games: 'clockwork_dash',
  nocturne_dreamers_table: 'dream_echoes',
};
